<template>
  <div>
    <!-- Modal de Gestión de Recetas -->
    <CModal
      :show.sync="localShowModal"
      size="xl"
      color="info"
      :backdrop="true"
      :closeOnBackdrop="true"
      :centered="true"
    >
      <template #header>
        <h4 class="modal-title">
          Gestión de Recetas - {{ selectedProduct ? selectedProduct.name : '' }}
        </h4>
        <button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
      </template>
      
      <template #default>
        <div v-if="loading" class="text-center">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-2">Cargando recetas...</p>
        </div>

        <div v-else>
          <!-- Información del Producto -->
          <div class="card mb-4">
            <div class="card-header bg-light">
              <h6 class="mb-0">
                Información del Producto
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <strong>Producto:</strong> {{ selectedProduct ? selectedProduct.name : '' }}
                </div>
                <div class="col-md-6">
                  <strong>Total de Recetas:</strong> {{ recipes.length }}
                </div>
              </div>
            </div>
          </div>

          <!-- Grid de Presentaciones y Recetas -->
          <div class="row">
            <div 
              v-for="(recipe, index) in recipes" 
              :key="`recipe-${recipe.id}-${index}`"
              class="col-md-6 col-lg-4 mb-4"
            >
              <div class="card h-100 shadow-sm">
                <div class="card-header d-flex justify-content-between align-items-center">
                  <div>
                    <h6 class="mb-0">Receta #{{ recipe.id }}</h6>
                    <small class="text-muted">Presentación: {{ recipe.presentation || 'General' }}</small>
                  </div>
                  <CBadge 
                    color="info"
                    class="ms-2"
                  >
                    Configurada
                  </CBadge>
                </div>
                
                <div class="card-body">
                  <div class="mb-3">
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      </div>
                    <div class="mb-2">
                      <small class="text-muted d-block">Ingredientes:</small>
                      <span class="badge bg-secondary me-1" v-for="(ingredient, index) in getRecipeIngredients(recipe.id)" :key="`${recipe.id}-ingredient-${index}`">
                        {{ ingredient }}
                      </span>
                    </div>
                    
                    <div class="d-flex justify-content-between align-items-center">
                      <small class="text-success">
                        Costo promedio: S/ {{ getAverageCost(recipe.id) }}
                      </small>
                    </div>
                    <div class="mt-2">
                      <small class="text-muted d-block mb-1">Costos por presentación:</small>
                      <div class="d-flex flex-wrap gap-1">
                        <span class="badge bg-primary" v-if="getPresentationCost(recipe.id, 'personal') > 0">
                          Personal: S/ {{ getPresentationCost(recipe.id, 'personal') }}
                        </span>
                        <span class="badge bg-info" v-if="getPresentationCost(recipe.id, 'mediana') > 0">
                          Mediana: S/ {{ getPresentationCost(recipe.id, 'mediana') }}
                        </span>
                        <span class="badge bg-warning" v-if="getPresentationCost(recipe.id, 'grande') > 0">
                          Grande: S/ {{ getPresentationCost(recipe.id, 'grande') }}
                        </span>
                        <span class="badge bg-danger" v-if="getPresentationCost(recipe.id, 'familiar') > 0">
                          Familiar: S/ {{ getPresentationCost(recipe.id, 'familiar') }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="card-footer">
                  <div class="d-grid gap-2">
                    <CButton
                      color="warning"
                      variant="outline"
                      size="sm"
                      @click="editarReceta(recipe)"
                    >
                      Editar
                    </CButton>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Mensaje si no hay recetas -->
          <div v-if="recipes.length === 0" class="text-center py-5">
            <h5>No hay recetas configuradas</h5>
            <p class="text-muted">Este producto no tiene recetas asociadas.</p>
          </div>
        </div>
      </template>
      
      <template #footer>
        <CButton color="secondary" @click="closeModal">
          Cerrar
        </CButton>
        
        <CButton 
          v-if="recipes.length === 0"
          color="success" 
          @click="crearRecetaGenerica"
        >
          Nueva Receta
        </CButton>
      </template>
    </CModal>


    <!-- Modal Recipe para crear/editar (hijo) -->
    <CModal
      :show.sync="showRecipeModal"
      size="xl"
      color="info"
      :backdrop="true"
      :closeOnBackdrop="true"
      :centered="true"
      style="max-width: 95vw; width: 95vw;"
    >
      <template #header>
        <h4 class="modal-title">
          {{ selectedRecipeForEdit ? 'Editar' : 'Crear' }} Receta - {{ selectedProduct ? selectedProduct.name : '' }}
          <span v-if="selectedPriceForRecipe" class="text-muted ms-2">({{ selectedPriceForRecipe.name }})</span>
        </h4>
        <button type="button" class="btn-close" @click="closeRecipeModal" aria-label="Close"></button>
      </template>
      
      <template #default>
        <Recipe
          v-if="showRecipeModal"
          :show-modal="true"
          :product-id="selectedProduct ? selectedProduct.id : null"
          :selected-product="selectedProduct"
          :selected-recipe="selectedRecipeForEdit"
          :selected-price="selectedPriceForRecipe"
          :available-prices="prices"
          @recipe-saved="handleRecipeSaved"
          @cancel="closeRecipeModal"
          ref="recipeComponent"
        />
      </template>
      
      <template #footer>
        <CButton color="secondary" @click="closeRecipeModal">
          Cancelar
        </CButton>
        
        <CButton 
          color="success" 
          @click="saveRecipeFromModal"
          :disabled="!canSaveRecipe"
        >
          {{ selectedRecipeForEdit ? 'Actualizar' : 'Guardar' }} Receta
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import Recipe from './Recipe.vue'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'GestionRecetas',
  components: {
    Recipe
  },
  props: {
    showModal: {
      type: Boolean,
      default: false
    },
    selectedProduct: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      recipes: [],
      prices: [],
      costsByPresentation: {}, // Para almacenar costos por presentación de cada receta
      
      // Estados de modales hijos
      showRecipeModal: false,
      
      // Datos para modales hijos
      selectedRecipeForEdit: null,
      selectedPriceForRecipe: null,
      
      // Presentaciones predefinidas
      predefinedPresentations: [
        { id: 'personal', name: 'Personal', size: 'Pequeño' },
        { id: 'mediano', name: 'Mediano', size: 'Mediano' },
        { id: 'grande', name: 'Grande', size: 'Grande' },
        { id: 'familiar', name: 'Familiar', size: 'Extra Grande' }
      ],
      
      // Estado local del modal
      localShowModal: false
    }
  },
  watch: {
    showModal: {
      handler(newVal) {
        this.localShowModal = newVal
        if (newVal && this.selectedProduct) {
          this.loadData()
        }
      },
      immediate: true
    },
    selectedProduct: {
      handler(newVal) {
        if (newVal && this.showModal) {
          this.loadData()
        }
      }
    }
  },
  methods: {
    ...mapActions(['get', 'post']),
    async loadData() {
      if (!this.selectedProduct || !this.selectedProduct.id) return
      
      this.loading = true
      try {
        // Solo cargar recetas del producto
        await this.loadRecipes()
      } catch (error) {
        console.error('Error loading data:', error)
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error al cargar las recetas del producto',
          life: 3000
        })
      } finally {
        this.loading = false
      }
    },

    async loadRecipes() {
      try {
        const response = await this.get({
          url: `${this.url}/recipes/byProduct/${this.selectedProduct.id}`,
          token: this.token
        })
        this.recipes = response.data || []
        
        // Cargar costos por presentación para cada receta
        for (const recipe of this.recipes) {
          await this.loadCostsByPresentation(recipe.id)
        }
      } catch (error) {
        console.error('Error loading recipes:', error)
        this.recipes = []
      }
    },

    async loadCostsByPresentation(recipeId) {
      try {
        const response = await this.get({
          url: `${this.url}/recipes/${recipeId}/costs-by-presentation`,
          token: this.token
        })
        
        if (response && response.error === false && response.data) {
          this.$set(this.costsByPresentation, recipeId, response.data)
        }
      } catch (error) {
        console.error('Error loading costs by presentation:', error)
      }
    },

    getPresentationCost(recipeId, presentation) {
      const costs = this.costsByPresentation[recipeId]
      if (!costs || !costs[presentation]) return 0
      return parseFloat(costs[presentation].cost_presentation || 0).toFixed(2)
    },

    getAverageCost(recipeId) {
      const costs = this.costsByPresentation[recipeId]
      if (!costs) return '0.00'
      
      const presentations = ['personal', 'mediana', 'grande', 'familiar']
      let totalCost = 0
      let presentationCount = 0
      
      presentations.forEach(presentation => {
        if (costs[presentation] && costs[presentation].cost_presentation > 0) {
          totalCost += parseFloat(costs[presentation].cost_presentation)
          presentationCount++
        }
      })
      
      return presentationCount > 0 ? (totalCost / presentationCount).toFixed(2) : '0.00'
    },


    getRecipeIngredients(recipeId) {
      const recipe = this.recipes.find(r => r.id === recipeId)
      if (!recipe || !recipe.details) return []
      
      // Agrupar por supply_id o product_id para evitar duplicados
      const uniqueIngredients = {}
      recipe.details.forEach(detail => {
        // Determinar si es un producto o un insumo
        const isProduct = detail.product_id !== null && detail.product_id !== undefined
        const ingredientId = isProduct ? `product_${detail.product_id}` : `supply_${detail.supply_id}`
        
        if (!uniqueIngredients[ingredientId]) {
          let ingredientName = ''
          if (isProduct && detail.product) {
            ingredientName = detail.product.name
          } else if (!isProduct && detail.supply) {
            ingredientName = detail.supply.name
          } else {
            ingredientName = isProduct ? 'Producto' : 'Insumo'
          }
          
          // Truncar nombre si es muy largo
          if (ingredientName.length > 15) {
            ingredientName = ingredientName.substring(0, 15) + '...'
          }
          
          uniqueIngredients[ingredientId] = ingredientName
        }
      })
      
      return Object.values(uniqueIngredients).slice(0, 3)
    },


    async editarReceta(recipe) {
      // Load the full recipe data with all details
      try {
        const response = await this.get({
          url: `${this.url}/recipes/${recipe.id}/show`,
          token: this.token
        })
        
        if (response && response.error === false && response.data) {
          this.selectedRecipeForEdit = response.data
        } else {
          this.selectedRecipeForEdit = recipe // Fallback to the basic recipe data
        }
      } catch (error) {
        console.error('Error loading full recipe data:', error)
        this.selectedRecipeForEdit = recipe // Fallback to the basic recipe data
      }
      
      this.selectedPriceForRecipe = null // Ya no dependemos de precios
      this.showRecipeModal = true
    },


    crearRecetaGenerica() {
      // Crear receta genérica sin presentación específica
      this.selectedRecipeForEdit = null
      this.selectedPriceForRecipe = null
      this.showRecipeModal = true
      
      // Forzar el reset del formulario en el próximo tick
      this.$nextTick(() => {
        if (this.$refs.recipeComponent) {
          this.$refs.recipeComponent.resetForm()
        }
      })
    },

    closeModal() {
      this.localShowModal = false
      this.$emit('close-modal')
    },


    closeRecipeModal() {
      this.showRecipeModal = false
      this.selectedRecipeForEdit = null
      this.selectedPriceForRecipe = null
    },


    async handleRecipeSaved() {
      // Recargar las recetas cuando se guarda una nueva
      await this.loadRecipes()
      this.closeRecipeModal()
      
      Swal.fire({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
        icon: 'success',
        title: 'Receta guardada correctamente'
      })
    },

    saveRecipeFromModal() {
      // Llamar al método saveRecipe del componente Recipe
      if (this.$refs.recipeComponent) {
        this.$refs.recipeComponent.saveRecipe()
      }
    }
  },

  computed: {
    canSaveRecipe() {
      // Verificar si el componente Recipe puede guardar
      if (this.$refs.recipeComponent) {
        return !this.$refs.recipeComponent.isSaving
      }
      return true
    },
    ...mapGetters({
      url: 'get__url',
      token: 'get__token',
      company_id: 'get_company'
    })
  }
}
</script>

<style scoped>
.card {
  transition: transform 0.2s;
}

.card:hover {
  transform: translateY(-2px);
}

.badge {
  font-size: 0.7em;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

.card-header {
  border-bottom: 1px solid #dee2e6;
}

.card-footer {
  border-top: 1px solid #dee2e6;
  background-color: #f8f9fa;
}

.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
}
</style>
